/* Circadia client analytics emit harness.
   ============================================================================
   Phase 7 Site-Wide Search (S7) — the storefront half of the client→server
   analytics EVENT BRIDGE.

   Exposes window.Circadia.analytics.emit(eventName, properties) — a
   fire-and-forget beacon that POSTs ONE web-tracking event to
   /apps/dashboard/event (App Proxy → backend /proxy/event → emit() ledger).

   The search modal (S8) calls this on a committed search:
     window.Circadia.analytics.emit('search_performed',
       { query: term, results_count: n, source: 'modal' });

   ── Design contract ─────────────────────────────────────────────────────────
   * FIRE-AND-FORGET: emit() never throws, never returns a blocking promise the
     caller must await, and swallows every network/serialisation error. A failed
     beacon must NEVER affect the UX that triggered it (the search isolation bar).
   * keepalive: true so the POST survives a page navigation right after a search.
   * IDENTITY is anonymous + client-only here: we mint a persistent visitorId
     (localStorage) + a per-tab sessionId (sessionStorage). The SERVER attaches
     the real logged-in customer from the signed App-Proxy param — we never send
     a customer id from the browser (it would be untrusted anyway).
   * CONSENT (the HARD GATE): we read the effective per-category consent from the
     single source — window.Circadia.consent (consent-config.js, loaded first).
     emit() fires NOTHING and visitorId() MINTS NOTHING unless analytics consent is
     granted (an explicit cookie choice, or — under the PIPEDA launch policy — the
     implied-on default). We fail CLOSED: if consent-config is somehow absent, the
     snapshot is all-false and nothing is recorded. The consent snapshot we attach
     is what the server bridge re-checks, so client + server agree.

   No external dependencies. IIFE, matches the surrounding circadia-chrome.js /
   case-aggregation.js style. Loaded with `defer` from layout/theme.liquid. */

(function () {
  'use strict';

  // App Proxy path under livecircadia.ca/apps/dashboard/* — Shopify appends the
  // signed HMAC params as it forwards to the backend's /proxy/event router.
  var EVENT_URL = '/apps/dashboard/event';

  // Visitor identity (S2): a first-party COOKIE is the primary store — the server
  // re-issues it via Set-Cookie on each beacon, which is more ITP-durable than a
  // JS-set value. localStorage is kept as a mirror + a migration source for the
  // pre-S2 id. Session identity is a cookie carrying {id,last,utm} so the 30-min
  // inactivity / UTM-change boundary survives across tabs.
  var VISITOR_COOKIE = 'circadia_vid';
  var VISITOR_KEY = 'circadia_visitor_id'; // legacy localStorage (pre-S2) — migrated
  var SESSION_COOKIE = 'circadia_sid';
  var VISITOR_MAX_AGE = 60 * 60 * 24 * 400; // ~13 months
  var SESSION_MAX_AGE = 60 * 60 * 24; // 24h cap (the 30-min idle rule expires it sooner)
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30-min inactivity boundary

  // ── Cookie helpers (first-party, Secure on https, SameSite=Lax, JS-readable) ─
  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (_) {
      return null;
    }
  }
  function setCookie(name, value, maxAgeSeconds) {
    try {
      var secure =
        window.location && window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie =
        name +
        '=' +
        encodeURIComponent(value) +
        '; Max-Age=' +
        maxAgeSeconds +
        '; Path=/; SameSite=Lax' +
        secure;
    } catch (_) {
      /* cookies disabled — ids degrade to per-call ephemerals */
    }
  }

  // ── UUID ────────────────────────────────────────────────────────────────
  function uuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (_) {
      /* fall through to the manual generator */
    }
    // RFC4122-ish v4 fallback for browsers without crypto.randomUUID.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (ch) {
      var r = (Math.random() * 16) | 0;
      var v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ── UTM / attribution (S4) ────────────────────────────────────────────────
  var ATTR_FIRST_COOKIE = 'circadia_attr_first'; // first-touch (immutable once set)
  var ATTR_LAST_COOKIE = 'circadia_attr_last'; // last-touch (overwritten each campaign)
  var ATTR_MAX_AGE = 60 * 60 * 24 * 180; // ~6 months attribution window
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function parseUtms() {
    var out = {};
    try {
      var p = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach(function (k) {
        var v = p.get(k);
        if (v) out[k] = v;
      });
    } catch (_) {
      /* no UTMs */
    }
    return out;
  }

  // The UTM signature of the CURRENT url — used to detect a campaign re-entry for
  // the session boundary (S2). '' when no UTM.
  function utmSignature() {
    var u = parseUtms();
    var parts = [];
    ['utm_source', 'utm_medium', 'utm_campaign'].forEach(function (k) {
      if (u[k]) parts.push(k + '=' + u[k]);
    });
    return parts.join('&');
  }

  // Persist first-touch + last-touch attribution (consent-gated — a persistent
  // attribution cookie is tracking). Idempotent: last-touch overwrites only when
  // the current url carries UTMs; first-touch is written once.
  function captureAttribution() {
    if (!analyticsGranted()) return;
    var u = parseUtms();
    if (Object.keys(u).length === 0) return; // no campaign on this landing
    try {
      u.ts = Date.now();
      u.landing_page = window.location ? window.location.pathname : null;
      var payload = JSON.stringify(u);
      if (!getCookie(ATTR_FIRST_COOKIE)) setCookie(ATTR_FIRST_COOKIE, payload, ATTR_MAX_AGE);
      setCookie(ATTR_LAST_COOKIE, payload, ATTR_MAX_AGE); // last-touch always refreshes
    } catch (_) {
      /* attribution is best-effort */
    }
  }

  // The campaign to attach to an event: the current url's, else last-touch. The
  // server resolves it into dim_campaigns (resolveCampaignDim).
  function currentCampaign() {
    var u = parseUtms();
    if (u.utm_campaign) return u.utm_campaign;
    try {
      var last = getCookie(ATTR_LAST_COOKIE);
      if (last) {
        var obj = JSON.parse(last);
        return obj && obj.utm_campaign ? obj.utm_campaign : null;
      }
    } catch (_) {
      /* fall through */
    }
    return null;
  }

  // ── Consent (HARD GATE) — single source: window.Circadia.consent ──────────
  // Fail-closed: if consent-config.js is absent, nothing is granted.
  function analyticsGranted() {
    try {
      return !!(
        window.Circadia &&
        window.Circadia.consent &&
        typeof window.Circadia.consent.analyticsGranted === 'function' &&
        window.Circadia.consent.analyticsGranted()
      );
    } catch (_) {
      return false;
    }
  }

  function consentSnapshot() {
    try {
      if (
        window.Circadia &&
        window.Circadia.consent &&
        typeof window.Circadia.consent.snapshot === 'function'
      ) {
        return window.Circadia.consent.snapshot();
      }
    } catch (_) {
      /* fall through to fail-closed */
    }
    return { analytics: false, marketing: false, preferences: false };
  }

  // visitor id: READING an already-persisted id is allowed regardless (reading is
  // not minting); MINTING a new persistent id requires analytics consent — the
  // HARD GATE. Primary store is the first-party cookie (server-refreshed for ITP
  // durability); the legacy localStorage id is migrated into the cookie once.
  // Returns null when no id exists and consent is absent.
  function visitorId() {
    var fromCookie = getCookie(VISITOR_COOKIE);
    if (fromCookie) return fromCookie;
    // Migrate a pre-S2 localStorage id (already minted under consent) → cookie.
    var legacy = null;
    try {
      legacy = window.localStorage.getItem(VISITOR_KEY);
    } catch (_) {
      /* storage blocked */
    }
    if (legacy) {
      setCookie(VISITOR_COOKIE, legacy, VISITOR_MAX_AGE);
      return legacy;
    }
    if (!analyticsGranted()) return null; // never MINT without consent
    var fresh = uuid();
    setCookie(VISITOR_COOKIE, fresh, VISITOR_MAX_AGE);
    try {
      window.localStorage.setItem(VISITOR_KEY, fresh);
    } catch (_) {
      /* mirror best-effort */
    }
    return fresh;
  }

  function readSessionState() {
    var raw = getCookie(SESSION_COOKIE);
    if (!raw) return null;
    try {
      var s = JSON.parse(raw);
      return s && typeof s === 'object' && s.id ? s : null;
    } catch (_) {
      return null;
    }
  }

  // session id: a real session per the blueprint — a NEW session starts on 30-min
  // inactivity OR a UTM change (campaign re-entry). MINTING a new session id is
  // consent-gated; reading/continuing an existing one is not.
  function sessionId() {
    var now = Date.now();
    var sig = utmSignature();
    var state = readSessionState();
    var needNew =
      !state ||
      now - (state.last || 0) > SESSION_TIMEOUT_MS || // 30-min inactivity
      (sig && sig !== (state.utm || '')); // UTM change → new session

    if (!needNew) {
      // Same session — bump last-activity (persist only with consent).
      if (analyticsGranted()) {
        state.last = now;
        setCookie(SESSION_COOKIE, JSON.stringify(state), SESSION_MAX_AGE);
      }
      return state.id;
    }
    // A new session id is a MINT → requires consent. Without it, fall back to any
    // existing id (read-only) or null.
    if (!analyticsGranted()) return state && state.id ? state.id : null;
    var fresh = { id: uuid(), last: now, utm: sig };
    setCookie(SESSION_COOKIE, JSON.stringify(fresh), SESSION_MAX_AGE);
    return fresh.id;
  }

  // ── Device type — coarse, best-effort (server treats null as unknown) ─────
  function deviceType() {
    try {
      var ua = navigator.userAgent || '';
      if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
      if (/iPad|Tablet/i.test(ua)) return 'tablet';
      return 'desktop';
    } catch (_) {
      return 'unknown';
    }
  }

  // ── The beacon ────────────────────────────────────────────────────────────
  // Returns nothing; callers MUST NOT depend on completion. Every failure path
  // is swallowed so the triggering UX (e.g. a search) is never coupled to it.
  // `extra` (optional) carries top-level identity fields the bridge reads
  // directly — currently experiment_key / variant_key for experiment_exposed.
  function emit(eventName, properties, extra) {
    try {
      if (typeof eventName !== 'string' || eventName.length === 0) return;

      // HARD GATE: no analytics event fires — and (via visitorId/sessionId below)
      // no identity is minted — unless analytics consent is granted. Under the
      // PIPEDA launch policy this is implied-on by default; under law25 it is off
      // until the visitor explicitly accepts.
      if (!analyticsGranted()) return;

      // S4: persist landing attribution (first/last-touch) before attaching the
      // campaign to the beacon.
      captureAttribution();

      var payload = {
        event_id: uuid(),
        event_name: eventName,
        timestamp: new Date().toISOString(),
        visitor_id: visitorId(),
        session_id: sessionId(),
        page_url: window.location ? window.location.href : null,
        page_path: window.location ? window.location.pathname : null,
        referrer: document.referrer || null,
        device_type: deviceType(),
        utm_campaign: currentCampaign(),
        properties: properties && typeof properties === 'object' ? properties : {},
        consent_snapshot: consentSnapshot(),
      };

      // Top-level identity extras (whitelisted) — e.g. experiment_key/variant_key
      // for experiment_exposed, which the bridge resolves into experiment dims.
      if (extra && typeof extra === 'object') {
        if (extra.experiment_key) payload.experiment_key = String(extra.experiment_key);
        if (extra.variant_key) payload.variant_key = String(extra.variant_key);
      }

      fetch(EVENT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        keepalive: true, // survive an immediate page navigation
        body: JSON.stringify(payload),
      }).catch(function () {
        /* fire-and-forget — never surface a beacon failure */
      });
    } catch (_) {
      /* never let analytics throw into a caller */
    }
  }

  // Namespace under window.Circadia (shared with circadia-chrome.js /
  // case-aggregation.js). Defensive create — script order is defer, but other
  // code may initialise the namespace first.
  if (!window.Circadia) window.Circadia = {};
  if (!window.Circadia.analytics) window.Circadia.analytics = {};
  window.Circadia.analytics.emit = emit;
  // Expose the id/consent helpers too — consumers (the consent banner, the S3
  // event catalog) read these; visitorId/sessionId self-gate on consent.
  window.Circadia.analytics.visitorId = visitorId;
  window.Circadia.analytics.sessionId = sessionId;
  window.Circadia.analytics.consentSnapshot = consentSnapshot;
  window.Circadia.analytics.analyticsGranted = analyticsGranted;
})();
